// Zentraler Zustand. Jede Änderung am Projekt läuft über commit(): so gibt es Undo/Redo,
// und die Yjs-Synchronisation sieht genau eine Stelle, an der sich das Projekt ändert.
import { create } from 'zustand';
import type { Project, Track, Clip, User, Presence } from '@srs/shared';
import { demoProject } from './demo.ts';

export type View = 'camp' | 'session' | 'compose' | 'klang' | 'mix' | 'splits' | 'ar' | 'katalog' | 'dante';

export interface Transport {
  playing: boolean; recording: boolean; position: number; metronome: boolean; countIn: boolean;
  /** Pre-/Post-Roll in Takten (0 = aus), wie in Pro Tools */
  preRoll?: number; postRoll?: number;
  /** Loop-Recording: jeder Durchgang ein Take */
  loopRecord?: boolean;
  /** MIDI Merge: neue Noten zu vorhandenen hinzufügen statt ersetzen */
  midiMerge?: boolean;
  /** Input Quantize: MIDI beim Aufnehmen auf das Raster des MIDI-Editors */
  inputQuantize?: boolean;
}

/** Undo-Verlauf; labels parallel zu past/future für die Anzeige („Rückgängig: Spur löschen“) */
interface History { past: Project[]; future: Project[]; pastLabels?: string[]; futureLabels?: string[] }

export interface StudioState {
  user: User;
  token: string | null;
  /** SRS-Sitzung (sit_…) für Abfragen direkt bei Smart Royalty Systems */
  srsSitzung: string | null;
  project: Project;
  history: History;
  view: View;
  selection: { clipIds: string[]; trackId: string | null };
  transport: Transport;
  zoom: number; // Pixel pro Sekunde
  peers: Presence[];
  online: { server: boolean; collab: boolean; bridge: boolean };
  /** Mixer (Taste X) und geöffnetes Plugin-Fenster */
  mixerOpen: boolean;
  pluginWin: { channel: string; slotId: string } | null;
  setMixerOpen(open: boolean): void;
  openPlugin(w: { channel: string; slotId: string } | null): void;
  setView(v: View): void;
  /** Projektänderung mit Undo-Eintrag. label erscheint im Verlauf. */
  commit(label: string, fn: (p: Project) => void, opts?: { remote?: boolean; merge?: boolean; silent?: boolean }): void;
  replaceProject(p: Project, remote?: boolean): void;
  undo(): void;
  redo(): void;
  select(clipIds: string[], trackId?: string | null): void;
  setTransport(t: Partial<Transport>): void;
  setZoom(z: number): void;
  setPeers(p: Presence[]): void;
  setOnline(o: Partial<StudioState['online']>): void;
  setAuth(token: string, user: User, srsSitzung?: string | null): void;
  logout(): void;
  /** Lokale Medien-Id nach dem Hochladen durch die Server-Id ersetzen (auch im Verlauf, ohne eigenen Rückgängig-Schritt) */
  replaceSourceId(from: string, to: string): void;
}

const MAX_UNDO = 200;
const clone = <T,>(x: T): T => structuredClone(x);
let lastMerge = { label: '', at: 0 };

/** Wird von der Zusammenarbeit gesetzt, um lokale Änderungen weiterzugeben. */
export const projectListeners = new Set<(p: Project, remote: boolean) => void>();

const guest: User = { id: 'u-gast', name: 'Gast', role: 'producer', color: '#003399' };

/** Anmeldung im Browser merken (Token ist serverseitig zeitlich begrenzt) */
const SESSION_KEY = 'srs.session';
function loadSession(): { token: string; user: User; srsSitzung?: string | null } | null {
  try { const s = JSON.parse(localStorage.getItem(SESSION_KEY) ?? 'null'); return s?.token && s?.user ? s : null; } catch { return null; }
}
function saveSession(s: { token: string; user: User; srsSitzung?: string | null } | null) {
  try { if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s)); else localStorage.removeItem(SESSION_KEY); } catch { /* privat */ }
}
const restored = loadSession();

export const useStudio = create<StudioState>()((set, get) => ({
  user: restored?.user ?? guest,
  token: restored?.token ?? null,
  srsSitzung: restored?.srsSitzung ?? null,
  project: demoProject(guest.id),
  history: { past: [], future: [] },
  view: 'session',
  selection: { clipIds: [], trackId: null },
  transport: { playing: false, recording: false, position: 0, metronome: false, countIn: true },
  zoom: 60,
  peers: [],
  online: { server: false, collab: false, bridge: false },
  mixerOpen: false,
  pluginWin: null,
  setMixerOpen: (mixerOpen) => set({ mixerOpen, pluginWin: mixerOpen ? get().pluginWin : null }),
  openPlugin: (pluginWin) => set({ pluginWin }),

  setView: (view) => set({ view }),
  commit(label, fn, opts = {}) {
    const prev = get().project;
    const next = clone(prev);
    fn(next);
    next.version = prev.version + 1;
    next.updatedAt = new Date().toISOString();
    const nowMs = performance.now();
    // Fader-Bewegungen o. ä. zu einem Undo-Schritt zusammenfassen
    const merge = opts.merge && lastMerge.label === label && nowMs - lastMerge.at < 800;
    lastMerge = { label, at: nowMs };
    const h = get().history, pl = h.pastLabels ?? [];
    const keep = opts.remote || merge || opts.silent;
    const past = keep ? h.past : [...h.past.slice(-MAX_UNDO + 1), prev];
    const pastLabels = keep ? pl : [...pl.slice(-MAX_UNDO + 1), label];
    set({ project: next, history: { past, pastLabels, future: opts.remote || opts.silent ? h.future : [], futureLabels: opts.remote || opts.silent ? h.futureLabels : [] } });
    projectListeners.forEach((l) => l(next, !!opts.remote));
  },
  replaceProject(p, remote = true) {
    set({ project: p });
    projectListeners.forEach((l) => l(p, remote));
  },
  undo() {
    const { past, future, pastLabels = [], futureLabels = [] } = get().history;
    if (!past.length) return;
    const prev = past[past.length - 1];
    set({ project: prev, history: { past: past.slice(0, -1), future: [get().project, ...future], pastLabels: pastLabels.slice(0, -1), futureLabels: [pastLabels[pastLabels.length - 1] ?? '', ...futureLabels] } });
    projectListeners.forEach((l) => l(prev, false));
  },
  redo() {
    const { past, future, pastLabels = [], futureLabels = [] } = get().history;
    if (!future.length) return;
    const next = future[0];
    set({ project: next, history: { past: [...past, get().project], future: future.slice(1), pastLabels: [...pastLabels, futureLabels[0] ?? ''], futureLabels: futureLabels.slice(1) } });
    projectListeners.forEach((l) => l(next, false));
  },
  select: (clipIds, trackId = null) => set({ selection: { clipIds, trackId } }),
  setTransport: (t) => set({ transport: { ...get().transport, ...t } }),
  setZoom: (zoom) => set({ zoom: Math.max(8, Math.min(400, zoom)) }),
  setPeers: (peers) => set({ peers }),
  setOnline: (o) => set({ online: { ...get().online, ...o } }),
  setAuth: (token, user, srsSitzung = null) => { saveSession({ token, user, srsSitzung }); set({ token, user, srsSitzung }); },
  logout: () => { saveSession(null); set({ token: null, srsSitzung: null, user: guest, peers: [] }); },
  replaceSourceId(from, to) {
    const fix = (p: Project) => {
      const busses = p.busses ?? [];
      if (!p.clips.some((c) => c.sourceId === from) && !p.takes.some((t) => t.sourceId === from) && ![...p.tracks, ...busses].some((t) => t.frozen?.sourceId === from || t.bridgeRender?.sourceId === from)) return p;
      const n = clone(p);
      n.clips.forEach((c) => { if (c.sourceId === from) c.sourceId = to; });
      n.takes.forEach((t) => { if (t.sourceId === from) t.sourceId = to; });
      [...n.tracks, ...(n.busses ?? [])].forEach((t) => { if (t.frozen?.sourceId === from) t.frozen = { ...t.frozen, sourceId: to }; if (t.bridgeRender?.sourceId === from) t.bridgeRender = { ...t.bridgeRender, sourceId: to }; });
      return n;
    };
    const { past, future } = get().history;
    const cur = get().project, next = fix(cur);
    set({ history: { ...get().history, past: past.map(fix), future: future.map(fix) } });
    if (next === cur) return;
    next.version = cur.version + 1;
    next.updatedAt = new Date().toISOString();
    set({ project: next });
    projectListeners.forEach((l) => l(next, false));
  },
}));

// ---- Hilfsfunktionen für Befehle ----
export const findTrack = (p: Project, id: string): Track | undefined => p.tracks.find((t) => t.id === id);
export const findClip = (p: Project, id: string): Clip | undefined => p.clips.find((c) => c.id === id);
/** Sekunden pro Viertel am Songanfang (für Tempo-Wechsel: spbAt aus @srs/shared) */
export const secondsPerBeat = (p: Project) => 60 / p.bpm;
export const projectLength = (p: Project) => Math.max(30, ...p.clips.map((c) => c.start + c.duration)) + 8;
